import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './TrialPage.css';

const PLANS = [
  { id: 'starter',      name: 'Starter',      monthly: 49,  yearly: 39 },
  { id: 'professional', name: 'Professional', monthly: 249, yearly: 199, recommended: true },
];

const BENEFITS = [
  { icon: '🛡️', title: 'Volledige vulnerability scanning', desc: 'OSV database — CVSS scores, FixedIn, aliassen' },
  { icon: '⚡', title: 'CI/CD integratie in 5 minuten', desc: 'GitHub Actions wizard met 6 ecosystems' },
  { icon: '🔔', title: 'Real-time alerts', desc: 'Slack & Teams webhooks bij nieuwe kritieke CVEs' },
  { icon: '🏛️', title: 'EU CRA compliance mapping', desc: 'Audit-bewijs in één rapport — klaar voor toezichthouders' },
  { icon: '🤖', title: 'AI assistent inbegrepen', desc: 'Stel vragen over je dependencies in natuurlijke taal' },
];

export default function TrialPage() {
  const [searchParams] = useSearchParams();
  const isEarlyAdopter = searchParams.get('earlyAdopter') === 'true';
  const planParam = searchParams.get('plan') || 'professional';

  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(
    PLANS.find(p => p.id === planParam) || PLANS[1]
  );
  const [form, setForm] = useState({ name: '', company: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens zijn.');
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registratie mislukt. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => navigate('/');

  const headingText = isEarlyAdopter
    ? 'Claim jouw Early Adopter plek'
    : 'Start je 14-dagen gratis trial';

  const subText = isEarlyAdopter
    ? `Professional voor €99/maand lifetime — vergrendel je prijs voor altijd.`
    : `Geen creditcard nodig. Na 14 dagen kies je een plan of ga je terug naar Community.`;

  return (
    <div className="trial-page">
      {/* ── Left panel ── */}
      <div className="trial-left">
        <div className="trial-logo" onClick={() => navigate('/website')} style={{ cursor: 'pointer' }}>
          <span className="trial-logo-icon">🛡️</span>
          <span className="trial-logo-text">DepGuard</span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <span className="trial-plan-pill">
            ✦ {selectedPlan.name} plan — €{selectedPlan.monthly}/maand
          </span>
          {isEarlyAdopter && (
            <span className="trial-early-pill">🚀 Early Adopter €99/mnd</span>
          )}
        </div>

        <h2>
          {isEarlyAdopter
            ? 'Eén van de eerste 10 klanten — profiteer voor altijd'
            : 'Alles om je software supply chain te beveiligen'}
        </h2>
        <p>
          {isEarlyAdopter
            ? 'Als early adopter ontvang je Professional voor €99/maand — lifetime, ook als de prijs later stijgt. Bedankt voor je vertrouwen.'
            : '14 dagen volledige toegang tot alle features. Geen creditcard, geen verplichting.'}
        </p>

        <ul className="trial-benefits">
          {BENEFITS.map((b, i) => (
            <li key={i} className="trial-benefit">
              <div className="trial-benefit-icon">{b.icon}</div>
              <div className="trial-benefit-text">
                <strong>{b.title}</strong>
                <span>{b.desc}</span>
              </div>
            </li>
          ))}
        </ul>

        <div className="trial-testimonial">
          <div className="trial-testimonial-text">
            "We moesten aantonen dat we CRA-compliant zijn voor onze Series A. DepGuard gaf ons
            in één dag een volledig auditrapport. Investeerders waren onder de indruk."
          </div>
          <div className="trial-testimonial-author">— Sarah K., CISO · FinTech startup</div>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className="trial-right">
        {success ? (
          /* ── Success state ── */
          <div className="trial-success">
            <div className="trial-success-icon">🎉</div>
            <h2>Account aangemaakt!</h2>
            <p>
              {isEarlyAdopter
                ? 'Welkom bij DepGuard Early Adopters. Je hebt je plek geclaimd!'
                : `Je 14-dagen ${selectedPlan.name} trial is geactiveerd. Geen creditcard nodig.`}
            </p>
            <ul className="trial-next-steps">
              {[
                'Upload je eerste SBOM of verbind een repository',
                'Stel een CI/CD webhook in via de GitHub Actions wizard',
                'Configureer een Slack webhook voor real-time alerts',
                isEarlyAdopter ? 'Ons team neemt binnen 24 uur contact op voor onboarding' : 'Verken het compliance dashboard',
              ].map((step, i) => (
                <li key={i} className="trial-next-step">
                  <span className="trial-next-step-num">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            <button className="trial-go-btn" onClick={handleGoToDashboard}>
              Naar het dashboard →
            </button>
          </div>
        ) : (
          /* ── Registration form ── */
          <>
            <div className="trial-form-header">
              <h1>{headingText}</h1>
              <p>{subText}</p>
            </div>

            {/* Plan selector (niet tonen bij early adopter) */}
            {!isEarlyAdopter && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>
                  Kies je plan
                </div>
                <div className="trial-plan-select">
                  {PLANS.map(p => (
                    <div
                      key={p.id}
                      className={`trial-plan-option${selectedPlan.id === p.id ? ' selected' : ''}`}
                      onClick={() => setSelectedPlan(p)}
                    >
                      <div className="trial-plan-option-name">
                        {p.recommended ? '⭐ ' : ''}{p.name}
                      </div>
                      <div className="trial-plan-option-price">€{p.monthly}/mnd</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isEarlyAdopter && (
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem', color: '#6ee7b7' }}>
                🚀 <strong>Early Adopter deal:</strong> Professional voor €99/maand lifetime (normaal €249/mnd). Slechts 10 plekken.
              </div>
            )}

            <form onSubmit={handleSubmit} className="trial-form">
              <div className="trial-field">
                <label>Volledige naam</label>
                <input
                  type="text"
                  placeholder="Jan de Vries"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                  autoFocus
                />
              </div>

              <div className="trial-field">
                <label>Bedrijfsnaam</label>
                <input
                  type="text"
                  placeholder="Jouw bedrijf BV"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                />
              </div>

              <div className="trial-field">
                <label>Zakelijk e-mailadres</label>
                <input
                  type="email"
                  placeholder="jan@bedrijf.nl"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>

              <div className="trial-field">
                <label>Wachtwoord (min. 8 tekens)</label>
                <input
                  type="password"
                  placeholder="••••••••••"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  minLength={8}
                />
              </div>

              {error && <div className="trial-error">⚠️ {error}</div>}

              <button type="submit" className="trial-submit" disabled={loading}>
                {loading ? 'Account aanmaken…' : isEarlyAdopter ? '🚀 Claim mijn plek' : `Start ${selectedPlan.name} trial →`}
              </button>

              <p className="trial-legal">
                Door te registreren ga je akkoord met onze{' '}
                <span style={{ color: '#818cf8', cursor: 'pointer' }}>Gebruiksvoorwaarden</span>{' '}
                en{' '}
                <span style={{ color: '#818cf8', cursor: 'pointer' }}>Privacybeleid</span>.
                {!isEarlyAdopter && ' Na 14 dagen downgrade je automatisch naar Community als je niet upgradet.'}
              </p>
            </form>

            <div className="trial-login-link">
              Heb je al een account?{' '}
              <button onClick={() => navigate('/login')}>Inloggen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
